import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { Category, CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { useIsDesktop } from "../../lib/hooks";
import { confirm } from "../../components/ui/Dialog";
import { Field, Input } from "../../components/ui/Input";
import { SettingsLayout, UnsavedBar } from "../../components/ui/Settings";
import { toast } from "../../components/ui/Toast";
import { IconFolder, IconTrash } from "../../components/icons";
import { PermissionsEditor, type PermissionDraft } from "./ChannelSettings";
import { permsOf } from "./hooks";
import { categoryTemplate } from "./permissionSync";
import { useRefreshCommunity } from "./settings/shared";

/*
 * Settings for a category, full screen like a channel's: Overview (name) and
 * Permissions. The permissions are a template: new channels in the category
 * copy them, and channels still synced with it follow every change.
 */

type Section = "overview" | "permissions";
type Draft = PermissionDraft & { name: string };

const draftOf = (c: Category): Draft => ({ name: c.name, ...categoryTemplate(c) });

export function CategorySettings({ structure, category, section: sectionParam }: { structure: CommunityStructure; category: Category; section?: string }) {
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const refresh = useRefreshCommunity(structure.community.id);
  const c = structure.community;
  const perms = permsOf(c.myPermissions, c.isOwner);
  const base = `/c/${c.id}/category/${category.id}/settings`;
  const source = useMemo(() => draftOf(category), [category]);
  const [draft, setDraft] = useState<Draft>(source);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(source), [source]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(source);
  const channels = structure.channels.filter((ch) => ch.categoryId === category.id);
  const synced = channels.filter((ch) => ch.permissionsSynced).length;

  const items = [
    perms.manageChannels && { id: "overview" as const, label: "Overview", keywords: "name" },
    (perms.manageRoles || perms.manageChannels) && { id: "permissions" as const, label: "Permissions", keywords: "private roles overrides access sync" },
  ].filter(Boolean) as { id: Section; label: string; keywords: string }[];
  const section = (items.some((i) => i.id === sectionParam) ? sectionParam : desktop ? items[0]?.id : undefined) as Section | undefined;
  const leave = () => navigate(`/c/${c.id}`);

  const close = async () => {
    if (dirty && !(await confirm({ title: "Discard changes?", description: "You have unsaved changes to this category.", confirmLabel: "Discard", danger: true }))) return;
    leave();
  };

  const save = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      for (const k of Object.keys(draft) as (keyof Draft)[]) if (JSON.stringify(draft[k]) !== JSON.stringify(source[k])) body[k] = draft[k];
      if (body.memberOverwrites) body.memberOverwrites = draft.memberOverwrites.filter((o) => o.allow || o.deny);
      if (body.overwrites) body.overwrites = draft.overwrites.filter((o) => o.allow || o.deny);
      const r = await api.patch<{ syncedChannelIds: string[] }>(`/api/categories/${category.id}`, body);
      await refresh();
      const n = r.syncedChannelIds?.length ?? 0;
      toast.show(n ? `Category saved · ${n} synced channel${n === 1 ? "" : "s"} updated` : "Category saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({ title: `Delete ${category.name}?`, description: "Channels in it move to the top of the list and keep their permissions.", confirmLabel: "Delete category", danger: true });
    if (!ok) return;
    try {
      await api.del(`/api/categories/${category.id}`);
      await refresh();
      navigate(`/c/${c.id}`, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (!items.length) return null;

  return (
    <SettingsLayout<Section>
      title={category.name}
      search={false}
      header={
        <div className="flex h-10 items-center gap-1.5 px-1.5 text-sm">
          <IconFolder className="icon size-4 shrink-0 text-fg-3" aria-hidden />
          <span className="truncate font-semibold">{draft.name || category.name}</span>
          <span className="shrink-0 text-fg-3" aria-hidden>
            ·
          </span>
          <span className="truncate text-xs text-fg-3">{c.name}</span>
        </div>
      }
      groups={[{ items }]}
      active={section}
      onSelect={(s) => navigate(`${base}/${s}`, { replace: desktop })}
      onBack={() => navigate(base)}
      onClose={() => void close()}
      sectionTitle={section ? items.find((i) => i.id === section)?.label : undefined}
      wide={section === "permissions"}
      footer={
        perms.manageChannels ? (
          <button onClick={() => void remove()} className="flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-sm text-danger hover:bg-danger-soft">
            <span className="flex-1 text-left">Delete category</span>
            <IconTrash className="icon size-[18px]" aria-hidden />
          </button>
        ) : undefined
      }
    >
      {section === "overview" && (
        <div className="space-y-7">
          <Field label="Category name">{(p) => <Input {...p} value={draft.name} maxLength={32} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}</Field>
        </div>
      )}
      {section === "permissions" && (
        <PermissionsEditor
          structure={structure}
          draft={draft}
          setDraft={(patch) => setDraft({ ...draft, ...patch })}
          canEditOverrides={perms.manageRoles}
          known={category.memberOverwrites}
          voice
          noun="category"
          banner={
            <p className="rounded-lg border border-line bg-surface px-4 py-3 text-sm text-fg-2">
              New channels in this category start with these permissions.{" "}
              {channels.length === 0
                ? "There are no channels in it yet."
                : synced === channels.length
                  ? `All ${channels.length} of its channels are synced and follow changes made here.`
                  : `${synced} of its ${channels.length} channels ${synced === 1 ? "is" : "are"} synced and follow changes made here; the others keep their own permissions.`}
            </p>
          }
        />
      )}
      <UnsavedBar show={dirty} saving={saving} onReset={() => setDraft(source)} onSave={() => void save()} />
    </SettingsLayout>
  );
}
